import { ProcessRespondentsApproval } from './../../../models/process-models/process-respondents-approval.model';
import { ProcessParticipationService } from './../../../services/process.participation.service';
import { Respondent } from './../../../models/respondent.model';
import { Subject, Observable } from 'rxjs';
import { ProcessService } from './../../../services/process.service';
import {
  ProcessRespondentSelectionCriterionType,
  ProcessAppraiseeApproval,
  ProcessParticipationApprovalRequirementType,
} from './../../../models/process-models';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Component,
  OnInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  TemplateRef,
} from '@angular/core';
import {
  EmployeeApprovalSearchRequest,
  Employee,
} from '../../../models/identity';
import { RouterPathHelper } from '../../../helpers/router-path-helper';
import { ConfirmationRespondentService } from '../../../services/confirmation-respondent.service';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-confirmation-respondents-detail',
  templateUrl: './confirmation-respondents-detail.component.html',
  styleUrls: ['./confirmation-respondents-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmationRespondentsDetailComponent implements OnInit {
  processRespondentsApproval$: Observable<ProcessRespondentsApproval>;
  processAppraiseeApproval$: Observable<ProcessAppraiseeApproval>;
  employeeApprovalSearchRequest: EmployeeApprovalSearchRequest = {};
  processAppraiseeDurationId: number;
  requirementType: number;
  respondents: Array<Respondent> = [];
  searchPattern$ = new Subject<string>();
  respondentSelectionType: any = ProcessRespondentSelectionCriterionType;
  processRespondentsApproval: Array<ProcessRespondentsApproval>;
  appraisee: Employee;
  approveDisabled: boolean = false;
  private approveClicked: boolean = false;

  modalRef: BsModalRef;

  private routePathHelper: RouterPathHelper;
  private readonly respondentsApprovalSearchPageComponent =
    'respondents-approval';
  private isEmployeeLoaded = false;
  constructor(
    private route: ActivatedRoute,
    private readonly router: Router,
    private processService: ProcessService,
    private readonly ref: ChangeDetectorRef,
    private readonly processParticipationService: ProcessParticipationService,
    private readonly confirmationService: ConfirmationRespondentService,
    private bsModalService: BsModalService,
    private toastr: ToastrService,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.routePathHelper = new RouterPathHelper(this.route);
    this.route.paramMap.subscribe((params) => {
      this.processAppraiseeDurationId = +params.get('id');
      this.requirementType = +this.route.snapshot.paramMap.get(
        'requirementType'
      );
      this.processRespondentsApproval$ = this.processService.getProcessRespondentsApproval(
        this.processAppraiseeDurationId
      );
    });
  }

  canShowError(processRespondentApproval: ProcessRespondentsApproval) {
    return (
      this.approveClicked &&
      !this.hasEnoughRespondents(processRespondentApproval)
    );
  }

  hasEnoughRespondents(processRespondentApproval: ProcessRespondentsApproval) {
    return (
      processRespondentApproval.respondents.length >=
      this.getApprovalvalue(processRespondentApproval)
    );
  }

  deleteRespondent(respondent: Employee) {
    this.processParticipationService
      .deleteFromApprove(
        this.processAppraiseeDurationId,
        respondent.id,
        this.requirementType
      )
      .subscribe((x) => {
        this.processRespondentsApproval.forEach((pp) => {
          const index = pp.respondents.findIndex((i) => i.id === respondent.id);
          if (index !== -1) {
            pp.respondents.splice(index, 1);
          }
        });
        this.ref.detectChanges();
      });
  }

  getIsRespondentsApprovalExists(
    processRespondentsApproval: Array<ProcessRespondentsApproval>
  ): boolean {
    this.processRespondentsApproval = processRespondentsApproval;
    return this.processRespondentsApproval !== null;
  }

  getSelectionColumnName(selectionCriterionType: number): Observable<any> {
    return this.confirmationService.getSelectionColumnTranslation(
      selectionCriterionType
    );
  }

  sortByRespondent(respondents: Array<Employee>): Array<Employee> {
    return respondents.sort((a, b) =>
      a.displayName.localeCompare(b.displayName)
    );
  }

  setSearchRequest(searchRequest: EmployeeApprovalSearchRequest) {
    this.employeeApprovalSearchRequest = searchRequest;
  }

  navigateToRespondentsApproval(
    processRespondentApproval: ProcessRespondentsApproval
  ) {
    const respondentsSearchPath = this.routePathHelper.getNavigationPath(
      this.processAppraiseeDurationId,
      this.respondentsApprovalSearchPageComponent
    );
    this.router.navigate([
      respondentsSearchPath,
      {
        selectionType: processRespondentApproval.selectionCriterionType,
        requirementType: this.requirementType,
      },
    ]);
  }

  approve() {
    this.approveDisabled = true;
    this.processService
      .approveProcessAppraiseeRespondents(
        this.processAppraiseeDurationId,
        this.requirementType
      )
      .subscribe((x) => {
        this.approveDisabled = false;
        this.getNavigationRoute();
      });
    if (this.modalRef) {
      this.modalRef.hide();
    }
  }

  tryApprove(modalTemplate: TemplateRef<any>) {
    this.approveClicked = true;
    if (this.canApprove()) {
      this.approve();
    } else if (
      this.requirementType !=
      ProcessParticipationApprovalRequirementType.ByAppraisee
    ) {
      this.openApproveModal(modalTemplate);
    }
  }

  getEmployee(appraisee: Employee) {
    this.appraisee = appraisee;
    if (appraisee && !this.isEmployeeLoaded) {
      this.isEmployeeLoaded = true;
      this.ref.detectChanges();
    }
  }

  canApprove(): boolean {
    if (!this.processRespondentsApproval) {
      return true;
    }

    const exceptCriteriaProcessRespondentsApprovalIndex = this.processRespondentsApproval.findIndex(
      (x) =>
        this.getCanAppraiseeBypassSubordinatesChecking(x) ||
        (+x.selectionCriterionType ===
          +ProcessRespondentSelectionCriterionType.TeamMinAmount &&
          x.respondents.length > 0)
    );
    const notEnoughRespondents = this.processRespondentsApproval.some(
      (x, i) =>
        i !== exceptCriteriaProcessRespondentsApprovalIndex &&
        +x.value > x.respondents.length
    );

    const error = this.getNotEnoughRespondentsErrorDescription();
    if (notEnoughRespondents) {
      this.toastr.error(error.details, error.title);
    }
    return !notEnoughRespondents;
  }

  private getNotEnoughRespondentsErrorDescription() {
    let error;
    const lang = this.translateService.currentLang;
    if (lang == 'en') {
      error = {
        title: 'Approve failed',
        details: 'There is less than minimum respondents nominated',
      };
    } else if (lang == 'ru') {
      error = {
        title: 'Ошибка',
        details: 'Кол-во номинированных респондентов меньше необходимого',
      };
    }
    return error;
  }

  getIsAnyRespondents(): boolean {
    return this.processRespondentsApproval.some(
      (x) => x.respondents.length > 0
    );
  }

  getCanAppraiseeBypassSubordinatesChecking(
    processRespondentsApproval: ProcessRespondentsApproval
  ): boolean {
    return (
      +processRespondentsApproval.selectionCriterionType ===
        +ProcessRespondentSelectionCriterionType.TeamMinAmount &&
      this.appraisee &&
      (!this.appraisee.isPerformanceManager ||
        processRespondentsApproval.respondents.length > 0)
    );
  }

  getApprovalvalue(
    processRespondentsApproval: ProcessRespondentsApproval
  ): number {
    return +processRespondentsApproval.selectionCriterionType ===
      +ProcessRespondentSelectionCriterionType.TeamMinAmount
      ? 1
      : +processRespondentsApproval.value;
  }

  private getNavigationRoute() {
    const parentUrl = this.route.snapshot.parent.routeConfig.path;
    this.router.navigate([
      `/${parentUrl}`,
      { requirementType: this.requirementType },
    ]);
  }

  private openApproveModal(modalTemplate: TemplateRef<any>) {
    this.modalRef = this.bsModalService.show(modalTemplate);
  }
}
